/**
 * useQuestionValidation Hook
 *
 * Provides validation logic for questions in daily exercises.
 * Validates question fields, question type-specific requirements, and COMPREHENSION logic.
 */

import {useCallback} from 'react';
import type {QuestionWithNewFields} from '../../Questions/QuestionTypes';
import {QUESTION_TYPE} from '../../../graphql/API';
import {validateChoicesKeywordsExist} from '../utils/comprehensionParser';
import {BackendQuestionType} from '../../../types/newQuestionType';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface UseQuestionValidationReturn {
  validateQuestion: (
    question: QuestionWithNewFields,
    questionIndex: number,
  ) => ValidationResult;
  validateAllQuestions: (
    questions: QuestionWithNewFields[],
  ) => ValidationResult;
  validateTestMetadata: (
    testName: string,
    testType: string | undefined,
    testDescription: string,
    testSummary: string,
  ) => ValidationResult;
}

/**
 * Custom hook for validating questions and test metadata
 */
export const useQuestionValidation = (): UseQuestionValidationReturn => {
  /**
   * Validates a single question
   */
  const validateQuestion = useCallback(
    (
      question: QuestionWithNewFields,
      questionIndex: number,
    ): ValidationResult => {
      const errors: string[] = [];

      // Label questions exactly like the tabs the user sees (`question_<index>`,
      // 0-based) plus the name when available, so error messages point at the
      // right question instead of a confusing off-by-one "Question N".
      const label =
        `question_${questionIndex}` +
        (question.name && question.name.trim()
          ? ` ("${question.name.trim()}")`
          : '');

      // Basic field validation
      if (!question.name || question.name.trim() === '') {
        errors.push(`${label}: Name is required`);
      }

      if (!question.text || question.text.trim() === '') {
        errors.push(`${label}: Question text is required`);
      }

      if (!question.type) {
        errors.push(`${label}: Question type is required`);
      }

      // Program validation (new field)
      if (!question.program || question.program.trim() === '') {
        errors.push(`${label}: Program selection is required`);
      }

      // Difficulty validation (new field)
      if (
        !question.difficulty ||
        !['EASY', 'NORMAL', 'HARD'].includes(question.difficulty)
      ) {
        errors.push(`${label}: Valid difficulty level is required`);
      }

      // Question type-specific validation
      switch (question.type) {
        case QUESTION_TYPE.CHOICES:
          // Must have 4 choices
          if (!question.choices || question.choices.length !== 4) {
            errors.push(`${label}: CHOICES type requires exactly 4 options`);
          } else {
            // Check that all choices are filled
            const emptyChoices = question.choices.filter(
              choice => !choice || choice.trim() === '',
            );
            if (emptyChoices.length > 0) {
              errors.push(`${label}: All 4 choice options must be filled`);
            }
          }
          break;

        case QUESTION_TYPE.BUILD_SENTENCE:
          // Must have 8 parts
          if (!question.choices || question.choices.length !== 8) {
            errors.push(
              `${label}: BUILD_SENTENCE type requires exactly 8 sentence parts`,
            );
          } else {
            // Check that all parts are filled
            const emptyParts = question.choices.filter(
              part => !part || part.trim() === '',
            );
            if (emptyParts.length > 0) {
              errors.push(`${label}: All 8 sentence parts must be filled`);
            }
          }
          break;

        case QUESTION_TYPE.COMPREHENSION:
        case BackendQuestionType.FILL_IN_THE_BLANKS as any:
          // Must have CHOICES keywords in text or textAI
          if (
            !validateChoicesKeywordsExist(
              question.text || '',
              question.textAI || '',
            )
          ) {
            errors.push(
              `${label}: COMPREHENSION type requires at least one CHOICES keyword (e.g., CHOICES1)`,
            );
          }

          // Validate payload.blanks structure (raw API format)
          if (
            !question.payload?.blanks ||
            question.payload.blanks.length === 0
          ) {
            errors.push(
              `${label}: COMPREHENSION type requires blanks to be defined`,
            );
          } else {
            // Check each blank has valid structure
            question.payload.blanks.forEach((blank, blankIndex) => {
              const blankName = blank.name || `Blank ${blankIndex + 1}`;

              if (!Array.isArray(blank.choices)) {
                errors.push(
                  `${label}: ${blankName} must have an array of choices`,
                );
                return;
              }

              if (blank.choices.length < 2) {
                errors.push(
                  `${label}: ${blankName} must have at least 2 choices`,
                );
              }

              if (blank.choices.length > 4) {
                errors.push(
                  `${label}: ${blankName} can have maximum 4 choices`,
                );
              }

              // Check that all choices are filled
              const emptyChoices = blank.choices.filter(
                choice => !choice || choice.trim() === '',
              );
              if (emptyChoices.length > 0) {
                errors.push(
                  `${label}: All choices for ${blankName} must be filled`,
                );
              }

              // Validate correctAnswer is set and matches one of the choices
              if (!blank.correctAnswer || blank.correctAnswer.trim() === '') {
                errors.push(
                  `${label}: ${blankName} must have a correct answer selected`,
                );
              } else if (!blank.choices.includes(blank.correctAnswer)) {
                errors.push(
                  `${label}: ${blankName} correct answer must match one of the choices`,
                );
              }
            });
          }
          break;

        case BackendQuestionType.REARRANGE_SENTENCE as any: {
          const words = (question.textAI || question.AiText || '')
            .trim()
            .split(/\s+/)
            .filter(Boolean);

          if (words.length < 2) {
            errors.push(
              `${label}: REARRANGE_SENTENCE requires AI text with at least 2 words`,
            );
          }
          break;
        }

        case QUESTION_TYPE.MISSING_LETTERS:
        case QUESTION_TYPE.MISSING_WORD:
          // Must have textAI filled
          if (!question.textAI || question.textAI.trim() === '') {
            errors.push(
              `${label}: ${question.type} requires AI text to be filled`,
            );
          }
          break;

        case QUESTION_TYPE.READ_SENTENCE:
        case 'DESCRIBE_IMAGE' as any:
        case QUESTION_TYPE.FREE_TEXT_ONLY_PRONUNCIATION:
          // Basic validation only (text and textAI)
          if (!question.textAI || question.textAI.trim() === '') {
            errors.push(`${label}: AI text is required`);
          }
          break;

        case QUESTION_TYPE.TRANSCRIBE_AND_SCORE:
          // AI text is optional, so no required validation for it
          break;

        default:
          // Unknown question type
          errors.push(`${label}: Unknown question type: ${question.type}`);
      }

      // Asset validation (optional but if present, should be complete)
      if (question.asset) {
        if (question.asset.fileUri && !question.asset.type) {
          errors.push(`${label}: Asset type is required when file is provided`);
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    },
    [],
  );

  /**
   * Validates all questions
   */
  const validateAllQuestions = useCallback(
    (questions: QuestionWithNewFields[]): ValidationResult => {
      const allErrors: string[] = [];

      // Check that there's at least one question
      if (!questions || questions.length === 0) {
        allErrors.push('At least one question is required');
        return {
          isValid: false,
          errors: allErrors,
        };
      }

      // Validate each question
      questions.forEach((question, index) => {
        const result = validateQuestion(question, index);
        if (!result.isValid) {
          allErrors.push(...result.errors);
        }
      });

      return {
        isValid: allErrors.length === 0,
        errors: allErrors,
      };
    },
    [validateQuestion],
  );

  /**
   * Validates test metadata (name, type, description, summary)
   */
  const validateTestMetadata = useCallback(
    (
      testName: string,
      testType: string | undefined,
      testDescription: string,
      testSummary: string,
    ): ValidationResult => {
      const errors: string[] = [];

      if (!testName || testName.trim() === '') {
        errors.push('Test name is required');
      }

      if (!testType) {
        errors.push('Test type is required');
      }

      if (!testDescription || testDescription.trim() === '') {
        errors.push('Test description is required');
      }

      if (!testSummary || testSummary.trim() === '') {
        errors.push('Test summary is required');
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    },
    [],
  );

  return {
    validateQuestion,
    validateAllQuestions,
    validateTestMetadata,
  };
};
